// Screen 2 — Casting Calls page (Figma 1-829).
// Lists open breakdowns near the geo-guessed location; every role row's
// Submit enters the flow via startFlow (which re-checks the deadline gate).

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import qs from "query-string";
import i18next from "i18next";
import { Trans } from "react-i18next";
import {
  getSubmissionFlowApi,
  IBreakdownRoleDto,
  ICastingCallDto,
  ICastingCallListDto,
  isMockApi,
} from "../../../shared/api/submission";
import { isNetworkError } from "../../../shared/http";
import { iRootState } from "../../../store";
import { getDeadlineInfo, getServerOffsetMs } from "../common/deadline";
import SubmissionLayout from "../common/SubmissionLayout";
import ScenarioLauncher from "../common/ScenarioLauncher";
import { isViewerAuthenticated } from "../common/viewerAuth";
import { fallbackTo, LANDING_BREAKDOWNS, LANDING_PARAM } from "../common/flowGuard";
import { trackSubmissionEvent } from "../analytics";
import { DESKTOP_WIDE, DESKTOP_XXL } from "../common/breakpoints";
import { openBreakdownUrl } from "../common/openBreakdownUrl";
import { dashboardUrl } from "../common/dashboard";
import VerifiedCheck from "../common/VerifiedCheck";
import { projectTypeLabel } from "../common/projectTypes";
import { membershipPrimary } from "../common/membershipButton";
import { reportEmailReturn, withoutEmailRef } from "../common/emailReturn";
import { alphabetical } from "../common/alphabetical";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
  match?: any;
}

const CastingCallsPage: React.FC<Props> = ({
  history,
  location,
  match,
  clientId,
  startFlow,
}) => {
  const [list, setList] = useState<ICastingCallListDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [startingRoleId, setStartingRoleId] = useState<string | null>(null);

  // QA override convention: ?dev&ctry=XX forces the requester country
  // (same spirit as setForceRequesterCountryCode in Registration.tsx).
  const query = qs.parse(location.search);
  // /submission/casting-calls/MX is the same thing as ?ctry=MX: a per-country
  // page with its own URL. The server still decides whether this visitor may
  // browse that country and answers with their own if not.
  const pathCountry = (match && match.params && match.params.ctry) || null;
  const forcedCountry =
    pathCountry || (query.ctry as string) || (query.countryCode as string) || null;
  // Optional breakdown-type filter (0 film & TV, 2 commercial, 3 theatre).
  const requestedType = /^[023]$/.test(String(query.type || ""))
    ? parseInt(query.type as string, 10)
    : undefined;
  // Where the visitor is — country, and the city or province that ranks the
  // list — is the API's call, made from the request's IP. This page sends
  // nothing about it: no city, no province, no list options. The only input is
  // a country from the URL (/casting-calls/MX or ?ctry=MX), which the API
  // still checks against its switch policy.
  const load = () => {
    setLoading(true);
    setError(null);
    getSubmissionFlowApi()
      .getCastingCalls({
        countryCode: forcedCountry ? forcedCountry.toUpperCase() : undefined,
        type: requestedType,
      })
      .then((data) => {
        setList(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  };

  // Back through a win-back or finish-later email with no role to name (the general email):
  // that email's return, once, then the eref leaves the address (emailReturn.ts).
  useEffect(() => {
    if (reportEmailReturn(location.search))
      history.replace({ pathname: location.pathname, search: withoutEmailRef(location.search) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackSubmissionEvent("submission_casting_calls_viewed", { forcedCountry });
    load();
    // Keyed on the URL's country and type: the country switcher navigates
    // between /casting-calls/CA and /casting-calls/US, which is the same route
    // and the same mounted page, so the list must reload on the change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedCountry, requestedType]);

  const onSubmitToRole = (call: ICastingCallDto, role: IBreakdownRoleDto) => {
    setStartingRoleId(role.roleId);
    trackSubmissionEvent("submission_role_selected", {
      breakdownId: call.breakdownId,
      roleId: role.roleId,
    });
    startFlow({
      breakdownId: call.breakdownId,
      roleId: role.roleId,
      entrySource: "castingCalls",
      history,
      search: location.search,
    }).catch(() => setStartingRoleId(null));
  };

  // A posting casting in more than one city says so: it is the difference
  // between "you cannot submit from here" and "you can, from either country".
  // But some postings cast in sixty cities, and sixty names under a title is a
  // wall of text on a phone (Spain, 2026-09-28 recording). The visitor's own
  // city leads, two more follow in alphabetical order (alphabetical.ts), and the rest is a count.
  const CITY_NAMES_SHOWN = 3;
  const cityLabel = (call: ICastingCallDto) => {
    const names = (call.cities || []).map((c) => c.cityName);
    if (names.length <= 1) return call.cityName;
    const ordered = [call.cityName, ...alphabetical(names.filter((n) => n !== call.cityName), (n) => n)];
    if (ordered.length <= CITY_NAMES_SHOWN) return ordered.join(" · ");
    const more = ordered.length - CITY_NAMES_SHOWN;
    return (
      ordered.slice(0, CITY_NAMES_SHOWN).join(" · ") +
      " " +
      i18next.t("+{{count}} more cities", { count: more })
    );
  };

  // Every title, open or greyed out, opens the breakdown's public page on the
  // classic site: the full breakdown (description, dates, every role) that this
  // list only summarises. A new tab, as on the boost page (decided
  // 2026-10-07): the flow itself never goes to a classic page, so the list
  // stays where it is. Verified casting directors get a green check.
  const renderTitle = (call: ICastingCallDto) => (
    <TitleLine>
      <CallName
        href={openBreakdownUrl(call.classicRef || call.breakdownId)}
        target="_blank"
        rel="noopener noreferrer"
        title={call.breakdownName}
      onClick={() =>
        trackSubmissionEvent("submission_casting_call_details_opened", {
          breakdownId: call.breakdownId,
        })
      }
    >
      {call.breakdownName}
      </CallName>
      {call.isVerifiedDirector && <VerifiedCheck />}
    </TitleLine>
  );

  // CA / US / MX, the current country rightmost. Only for a visitor who may
  // switch (the API lists more than one country, and the one shown is among
  // them); anyone else sees their own list and no control at all.
  const renderCountrySwitch = () => {
    if (!list || loading || error) return null;
    const options = (list.selectableCountries || []).map((c) => c.toUpperCase());
    const current = (list.countryCode || "").toUpperCase();
    if (options.length < 2 || options.indexOf(current) < 0) return null;
    const ordered = [...options.filter((c) => c !== current), current];
    const typeQuery = requestedType !== undefined ? `?type=${requestedType}` : "";
    return (
      <CountrySwitch aria-label={i18next.t("Country")}>
        {ordered.map((code, i) => (
          <React.Fragment key={code}>
            {i > 0 && <CountrySep aria-hidden="true">/</CountrySep>}
            {code === current ? (
              <CurrentCountry aria-current="page">{code}</CurrentCountry>
            ) : (
              <CountryLink
                href={`/submission/casting-calls/${code}${typeQuery}`}
                onClick={(e: any) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  trackSubmissionEvent("submission_casting_calls_country_switched", {
                    from: current,
                    to: code,
                  });
                  history.push(`/submission/casting-calls/${code}${typeQuery}`);
                }}
              >
                {code}
              </CountryLink>
            )}
          </React.Fragment>
        ))}
      </CountrySwitch>
    );
  };

  const renderCall = (call: ICastingCallDto, serverOffsetMs: number) => {
    const deadline = getDeadlineInfo(call.closingDateUtc, serverOffsetMs);
    const openRoles = call.roles.filter((r) => r.status === "Open");
    // Three different things, and they are not the same message. The clock is
    // authoritative for "closed" — a status of "Open" on a breakdown whose
    // deadline passed yesterday is a real backend state, and the list must not
    // invite a submission nobody will read.
    // Closed by status, expired on the clock, or open with nothing left to
    // submit to — three different facts, three different words.
    const statusClosed = call.status === "Closed";
    // Past the deadline but still taking submissions: Expired, and still open.
    // The real deadline is the urgency; the margin after it is never shown.
    const late = !statusClosed && !!call.isLate;
    const isClosed = statusClosed || (deadline.isClosed && !late);
    const allCast = !isClosed && openRoles.length === 0;
    const closedLabel = allCast
      ? i18next.t("All roles cast")
      : statusClosed
      ? i18next.t("Closed")
      : i18next.t("Expired");

    // A closed call used to be dropped from the list silently. Show it, greyed
    // out and unclickable: a visitor who saw the ad yesterday should find the
    // breakdown and read "Closed" rather than wonder whether the page is
    // broken. Nothing here can start a flow, so the wall is the card itself.
    if (isClosed || allCast) {
      return (
        <ClosedCard key={`${call.breakdownId}-${call.cityId}`}>
          <CallHeader>
            <CallInfo>
              <ProjectType>{projectTypeLabel(call.projectType)}</ProjectType>
              {renderTitle(call)}
              <CityLabel>{call.cityName}</CityLabel>
            </CallInfo>
            <ClosedPill>{closedLabel}</ClosedPill>
          </CallHeader>
          <ClosedNote>
            {allCast
              ? i18next.t("Every role here has been cast. The project is still shooting.")
              : i18next.t("You just missed this one.")}
          </ClosedNote>
        </ClosedCard>
      );
    }
    return (
      <CallCard key={`${call.breakdownId}-${call.cityId}`} late={late}>
        <CallHeader>
          <CallInfo>
            <ProjectType>{projectTypeLabel(call.projectType)}</ProjectType>
            {renderTitle(call)}
            <CityLabel>{cityLabel(call)}</CityLabel>
          </CallInfo>
          {late ? (
            <LatePill>{i18next.t("Expired")}</LatePill>
          ) : (
            <ClosesPill>{deadline.label}</ClosesPill>
          )}
        </CallHeader>
        {late && <LateNote>{i18next.t("The deadline has passed, but casting hasn't closed this one yet.")}</LateNote>}
        {openRoles.map((role) => (
          <RoleRow key={role.roleId}>
            <RoleName>{role.roleName}</RoleName>
            <SubmitButton
              disabled={startingRoleId !== null}
              onClick={() => onSubmitToRole(call, role)}
              aria-label={`${i18next.t("Submit to")} ${role.roleName} — ${call.breakdownName}`}
            >
              {startingRoleId === role.roleId
                ? i18next.t("One moment…")
                : i18next.t("Submit")}
            </SubmitButton>
          </RoleRow>
        ))}
      </CallCard>
    );
  };

  // This list is a taster, not the product: it shows what is open near the
  // visitor and nothing else — no sort, no filter, no paging. Say so at the
  // bottom rather than letting someone hunt for controls that are not there,
  // and point each visitor at the one place that does have them.
  const renderMoreNotice = () => {
    if (loading || error) return null;

    // NOTE for translators and for anyone editing the JSX below: <Trans>
    // numbers its placeholders by CHILD POSITION, so the text and the links
    // have to stay on one line each. A stray {" "} inserts an extra child and
    // silently shifts every index past it, which shows up as raw "<1>" markup
    // on screen. Resource keys: castingCallsMoreForVisitors / …ForMembers.
    if (isViewerAuthenticated(clientId)) {
      return (
        <MoreNotice>
          <Trans i18nKey="castingCallsMoreForMembers">
            For more breakdowns, to sort or filter please go to your <NoticeLink href={dashboardUrl()}>dashboard</NoticeLink>
          </Trans>
        </MoreNotice>
      );
    }

    // Both links carry page=breakdowns: whoever logs in or signs up from here
    // asked for more breakdowns, so that is where they should land. Log in also
    // keeps the rest of the querystring (market, campaign tags); page= is
    // merged, never appended twice.
    const loginParams = {
      ...qs.parse(location.search),
      [LANDING_PARAM]: LANDING_BREAKDOWNS,
    };
    const loginUrl = `${process.env.REACT_APP_CWB_Actor_Login}?${qs.stringify(loginParams)}`;
    const signupPath = fallbackTo(location.search, {
      [LANDING_PARAM]: LANDING_BREAKDOWNS,
    });
    // An in-app route, so a plain click goes through the router — but it stays
    // a real href, so middle-click and "open in new tab" still work.
    const onSignupClick = (e: any) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      history.push(signupPath);
    };
    return (
      <MoreNotice>
        <Trans i18nKey="castingCallsMoreForVisitors">
          For more breakdowns, to sort or filter please <NoticeLink href={loginUrl}>log in</NoticeLink> or <NoticeLink href={signupPath} onClick={onSignupClick}>sign up</NoticeLink>
        </Trans>
      </MoreNotice>
    );
  };

  const renderBody = () => {
    if (error) {
      return (
        <ErrorBox role="alert">
          <div>
            {isNetworkError(error)
              ? error.message
              : i18next.t("Something went wrong. Please try again.")}
          </div>
          <RetryButton onClick={load}>{i18next.t("Try again")}</RetryButton>
        </ErrorBox>
      );
    }
    if (loading || !list) {
      return (
        <div aria-busy="true">
          <SkeletonBlock />
          <SkeletonBlock />
          <SkeletonBlock />
        </div>
      );
    }

    const serverOffsetMs = getServerOffsetMs(list.serverTimeUtc);
    const cards = list.castingCalls
      .map((call) => renderCall(call, serverOffsetMs))
      .filter((card) => card !== null);

    if (cards.length === 0) {
      return (
        <EmptyState>
          <EmptyTitle>
            {list.cityName
              ? i18next.t("No open calls in {{city}} right now", { city: list.cityName })
              : i18next.t("No open calls right now")}
          </EmptyTitle>
          <EmptyText>
            {i18next.t("New breakdowns are posted daily. Check back soon.")}
          </EmptyText>
        </EmptyState>
      );
    }
    return <CardGrid>{cards}</CardGrid>;
  };

  return (
    <SubmissionLayout wide>
      <TitleRow>
        <PageTitle>{i18next.t("Casting calls")}</PageTitle>
        {renderCountrySwitch()}
      </TitleRow>
      {list && list.cityName && !loading && !error && (
        <PageSubtitle>
          {i18next.t("Open roles near {{city}}", { city: list.cityName })}
        </PageSubtitle>
      )}
      {renderBody()}
      {renderMoreNotice()}
      {isMockApi() && <ScenarioLauncher search={location.search} />}
    </SubmissionLayout>
  );
};

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 4px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #122640;
  margin: 0;
`;

// Small and quiet: three codes, the current one set in navy, the others links.
const CountrySwitch = styled.nav`
  flex: 0 0 auto;
  display: flex;
  align-items: baseline;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const CountrySep = styled.span`
  color: #b7c0cd;
  margin: 0 6px;
`;

const CurrentCountry = styled.span`
  color: #122640;
`;

const CountryLink = styled.a`
  color: #0584d7;
  text-decoration: none;
  cursor: pointer;
  padding: 4px 2px;
  border-radius: 3px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
  }
`;

// Title and check on one line; the title still ellipsizes, the check never does.
const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin: 2px 0;
`;

const PageSubtitle = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-bottom: 16px;
`;

// A phone gets one card per row. A large screen gets columns instead: a single
// 1000px-wide row would strand the Submit button at the far right, miles from
// the role name it belongs to.
const CardGrid = styled.div`
  @media all and (min-width: ${DESKTOP_WIDE}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    /* Row spacing stays on the card's own margin-bottom. */
    gap: 0 16px;
  }

  @media all and (min-width: ${DESKTOP_XXL}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const CallCard: any = styled.div`
  border: 1px solid ${(p: any) => (p.late ? "#efc58e" : "#e3e8ee")};
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 14px;
  background: ${(p: any) => (p.late ? "#fffaf3" : "transparent")};
`;

// Expired but still open (isLate): amber, not the grey of a missed one, and
// darker than the card's tint so 11px bold text holds about 6:1.
const LatePill = styled.span`
  flex: 0 0 auto;
  margin-left: 10px;
  white-space: nowrap;
  background-color: #ffe2b8;
  color: #7a3f00;
  font-size: 11px;
  font-weight: 700;
  border-radius: 12px;
  padding: 3px 10px;
`;

const LateNote = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #7a3f00;
  margin: -2px 0 8px;
`;

const ClosedCard = styled.div`
  border: 1px solid #eef1f5;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 14px;
  background: #f7f9fc;
  color: #8a97ab;
`;

const ClosedPill = styled.span`
  flex: 0 0 auto;
  margin-left: 10px;
  padding: 3px 8px;
  border-radius: 10px;
  background: #e3e8ee;
  color: #53627c;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

const ClosedNote = styled.div`
  font-size: 12px;
  margin-top: 6px;
`;

const CallHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ProjectType = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #53627c;
`;

// The column the title lives in. flex-basis auto plus min-width 0 is what lets
// it shrink below its content: without it the title's intrinsic width won, and
// with a fixed 240px cap on the title a longer pill ("Closes in 6 hours",
// "Closes in 22 days") was pushed past the card's right edge.
const CallInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// A link that still reads as the card's title: same size, weight and colour,
// underlined on hover and focus so it announces itself without turning every
// card blue. display:block keeps the one-line ellipsis working on an <a>.
const CallName = styled.a`
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  color: #122640;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  cursor: pointer;

  /* The app's global stylesheet colours a:link / a:visited, which outranks a
     single class; restate the title colour for those states. */
  &:link,
  &:visited {
    color: #122640;
  }

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const CityLabel = styled.div`
  font-size: 12px;
  color: #53627c;
`;

const ClosesPill = styled.span`
  flex: 0 0 auto;
  margin-left: 10px;
  white-space: nowrap;
  /* Same blue as the step card, for the same reason: white bold 11px on the
     old #0f9bf0 was about 3:1. */
  background-color: #0584d7;
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
  padding: 3px 10px;
`;

const RoleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eef1f5;
  padding: 10px 0;
`;

const RoleName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #122640;
`;

const SubmitButton = styled.button`
  ${membershipPrimary}
  padding: 10px 16px;
  min-height: 44px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #122640;
`;

const EmptyText = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-top: 8px;
`;

const MoreNotice = styled.div`
  margin: 20px 0 4px;
  padding-top: 16px;
  border-top: 1px solid #eef1f5;
  font-size: 13px;
  line-height: 20px;
  color: #53627c;
  text-align: center;
`;

// nowrap so a link never breaks across lines — "sign" at the end of one line
// and a lone "up" on the next reads as two things to tap.
const NoticeLink = styled.a`
  color: #0584d7;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorBox = styled.div`
  border: 1px solid #f0c0c0;
  background: #fdf3f3;
  border-radius: 8px;
  padding: 16px;
  font-size: 14px;
  color: #7a2c2c;
`;

const RetryButton = styled.button`
  margin-top: 12px;
  background-color: #0f9bf0;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 16px;
  cursor: pointer;
`;

const SkeletonBlock = styled.div`
  height: 110px;
  border-radius: 8px;
  background-color: #eef1f5;
  margin-bottom: 12px;
`;

interface StateProps {
  // Set when this session created an account at email verification. It is the
  // half of "signed in" that lives in this app; the other half is the shared
  // cwb-react session, which viewerAuth reads.
  clientId: number | null;
}

interface DispatchProps {
  startFlow: (payload: {
    breakdownId: string;
    roleId?: string;
    entrySource: string;
    history: any;
    search?: string;
  }) => Promise<any>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  clientId: state.submissionSignupModel.clientId,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  startFlow: dispatch.submissionFlowModel.startFlow,
});

export default connect(mapStateToProps, mapDispatchToProps)(CastingCallsPage);
